import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import moment from "moment";
import { ColDef } from "ag-grid-community";
import { CheckCircle2, MoreHorizontal, Search, Trash2, ExternalLink, MapPin } from "lucide-react";

import PaginationLayout from "../components/PaginationLayout";
import profile_placeholder from "../assets/images/profile_placeholder.png";
import henceforthApi from "../utils/henceforthApi";
import { GlobalContext } from "../context/Provider";

import AgGridTable from "../components/ui/AgGridTable";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

interface ReportedBy {
  _id?: string;
  name?: string;
  email?: string;
  profile_pic?: string;
}

interface RideDetail {
  _id?: string;
  address_from?: string;
  address_to?: string;
  date?: string;
  price?: number;
  driver_name?: string;
  driver_image?: string;
}

interface ReportedRideItem {
  _id: string;
  report_by?: ReportedBy;
  ride_detail?: RideDetail;
  is_resolved: boolean;
  message: string;
  created_at?: string;
}

const ReportedRidesPage = () => {
  const location = useLocation();
  const params: any = useParams();
  const limit = 10;
  const { authState, handleFilter, handlePagination, handleError } = useContext(GlobalContext);

  const [report, setReport] = useState<Array<ReportedRideItem>>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [readContent, setReadContent] = useState("");
  const [modalOpen, setModalOpen] = useState(false);

  if (authState.access_token) {
    henceforthApi.setToken(authState.access_token);
  }

  const newParam = new URLSearchParams(location.search);
  const query = new URLSearchParams(location.search);

  const fetchReportedRides = async () => {
    try {
      setLoading(true);
      let q = query.toString();
      let apiRes = await henceforthApi.Report.ride(
        newParam.toString(),
        q,
        limit,
        Number(params.page) - 1
      );
      setReport(apiRes.data.data || []);
      setTotalCount(apiRes.data.total_count || 0);
    } catch (error: any) {
      setReport([]);
      setTotalCount(0);
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id: string) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this reported ride record!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#006747",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result: any) => {
      if (result.isConfirmed) {
        try {
          await henceforthApi.Report.delRide(id);
          Swal.fire({
            title: "Report Deleted",
            text: "Reported ride deleted successfully",
            icon: "success",
            confirmButtonText: "OK",
          });
          fetchReportedRides();
        } catch (err: any) {
          Swal.fire({
            title: "Delete Failed",
            text: err?.response?.body?.error_description || "Failed to delete report",
            icon: "error",
            confirmButtonText: "Cancel",
          });
        }
      }
    });
  };

  const handleResolve = (id: string) => {
    const data = {
      is_resolved: true,
      _id: id,
    };
    henceforthApi.Report.resolveRide(data)
      .then(() => {
        Swal.fire({
          title: "Report Resolved",
          text: "Reported ride marked as resolved successfully",
          icon: "success",
          confirmButtonText: "OK",
        });
        fetchReportedRides();
      })
      .catch((err: any) => {
        Swal.fire({
          title: "Resolve Failed",
          text: err?.response?.body?.error_description || "Failed to resolve report",
          icon: "error",
          confirmButtonText: "Cancel",
        });
      });
  };

  useEffect(() => {
    fetchReportedRides();
  }, [params.page, location.search]);

  const columnDefs = useMemo<ColDef<ReportedRideItem>[]>(() => {
    const pageNum = Number(params.page) || 1;
    return [
      {
        headerName: "Sr No.",
        field: "_id",
        width: 75,
        minWidth: 70,
        maxWidth: 85,
        suppressSizeToFit: true,
        valueGetter: (p) => {
          if (!p.node || p.node.rowIndex === null || p.node.rowIndex === undefined) return "";
          return (pageNum - 1) * limit + (p.node.rowIndex + 1);
        },
      },
      {
        headerName: "Reported By",
        field: "report_by.name" as any,
        minWidth: 190,
        flex: 1.3,
        cellRenderer: (p: any) => {
          const rep = p.data?.report_by;
          const pic = rep?.profile_pic
            ? rep.profile_pic.startsWith("https")
              ? rep.profile_pic
              : henceforthApi.FILES.imageOriginal(rep.profile_pic)
            : profile_placeholder;
          const name = rep?.name || "Not Available";
          const email = rep?.email || "";
          return (
            <div className="flex items-center gap-2.5 py-1">
              <img
                src={pic}
                alt=""
                className="w-9 h-9 rounded-full object-cover border border-slate-200 shrink-0"
              />
              <div className="flex flex-col min-w-0">
                <span className="font-semibold text-slate-800 text-sm truncate">
                  {name}
                </span>
                {email && (
                  <span className="text-xs text-slate-500 truncate">{email}</span>
                )}
              </div>
            </div>
          );
        },
      },
      // {
      //   headerName: "Driver",
      //   field: "ride_detail.driver_name" as any,
      //   minWidth: 170,
      //   flex: 1.1,
      //   cellRenderer: (p: any) => {
      //     const ride = p.data?.ride_detail;
      //     const pic = ride?.driver_image
      //       ? ride.driver_image.startsWith("https")
      //         ? ride.driver_image
      //         : henceforthApi.FILES.imageOriginal(ride.driver_image)
      //       : profile_placeholder;
      //     const name = ride?.driver_name || "Not Available";
      //     return (
      //       <div className="flex items-center gap-2.5 py-1">
      //         <img
      //           src={pic}
      //           alt=""
      //           className="w-9 h-9 rounded-full object-cover border border-slate-200 shrink-0"
      //         />
      //         <span className="font-medium text-slate-700 text-sm truncate">
      //           {name}
      //         </span>
      //       </div>
      //     );
      //   },
      // },
      {
        headerName: "Ride Route & Details",
        field: "ride_detail.address_from" as any,
        minWidth: 260,
        flex: 1.8,
        cellRenderer: (p: any) => {
          const ride = p.data?.ride_detail;
          if (!ride) return <span className="text-slate-400 text-xs">Ride details unavailable</span>;
          const from = ride.address_from || "Unknown pickup";
          const to = ride.address_to || "Unknown drop";
          const rideDate = ride.date ? moment(Number(ride.date)).format("DD MMM YYYY") : "";
          const price = typeof ride.price === "number" ? `$${ride.price.toFixed(2)}` : ride.price ? `$${ride.price}` : "";

          return (
            <div className="flex flex-col py-1 gap-1 text-xs text-slate-700">
              <div className="flex items-center gap-1.5 font-medium truncate">
                <MapPin className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                <span className="truncate max-w-[120px]" title={from}>{from}</span>
                <span className="text-slate-400">→</span>
                <span className="truncate max-w-[120px]" title={to}>{to}</span>
              </div>
              <div className="flex items-center gap-3 text-slate-500 text-[11px]">
                {rideDate && <span>{rideDate}</span>}
                {price && <span className="font-semibold text-slate-700">{price}</span>}
                {ride._id && (
                  <Link
                    to={`/ride-details/${ride._id}`}
                    className="inline-flex items-center gap-0.5 text-emerald-600 hover:text-emerald-700 hover:underline font-medium"
                    title="View ride details"
                  >
                    <span>View Ride</span>
                    <ExternalLink className="w-3 h-3" />
                  </Link>
                )}
              </div>
            </div>
          );
        },
      },
      {
        headerName: "Report Reason / Query",
        field: "message",
        minWidth: 230,
        flex: 1.6,
        cellRenderer: (p: any) => {
          const msg = p.value || "No reason provided";
          const isLong = msg.length > 45;
          return (
            <div className="flex items-center gap-1.5 py-1 text-sm text-slate-600">
              <span className="truncate max-w-[220px]">
                {isLong ? `${msg.slice(0, 45)}...` : msg}
              </span>
              {isLong && (
                <button
                  type="button"
                  onClick={() => {
                    setReadContent(msg);
                    setModalOpen(true);
                  }}
                  className="text-xs font-semibold text-emerald-600 hover:text-emerald-700 underline shrink-0"
                >
                  Read more
                </button>
              )}
            </div>
          );
        },
      },
      {
        headerName: "Date Reported",
        field: "created_at",
        minWidth: 130,
        cellRenderer: (p: any) => {
          const val = p.value;
          if (!val) return <span className="text-slate-400 text-xs">N/A</span>;
          const formatted = moment(Number(val)).isValid()
            ? moment(Number(val)).format("DD MMM YYYY")
            : moment(val).isValid()
              ? moment(val).format("DD MMM YYYY")
              : "N/A";
          return <span className="text-xs text-slate-600">{formatted}</span>;
        },
      },
      {
        headerName: "Status",
        field: "is_resolved",
        minWidth: 120,
        cellRenderer: (p: any) => {
          const isResolved = p.value;
          return (
            <div className="flex items-center h-full">
              {isResolved ? (
                <Badge variant="success">RESOLVED</Badge>
              ) : (
                <Badge variant="warning">PENDING</Badge>
              )}
            </div>
          );
        },
      },
      {
        headerName: "Action",
        field: "_id",
        minWidth: 110,
        suppressSizeToFit: true,
        cellRenderer: (p: any) => {
          const row = p.data;
          const isResolved = row?.is_resolved;
          const id = row?._id;
          return (
            <div className="flex items-center h-full">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="table-action-dots p-1 text-slate-500 hover:text-slate-800 focus:outline-none"
                    aria-label="Actions"
                  >
                    <MoreHorizontal className="w-5 h-5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40">
                  {!isResolved && (
                    <DropdownMenuItem
                      onClick={() => handleResolve(id)}
                      className="gap-2 cursor-pointer text-emerald-700 font-medium"
                    >
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      <span>Resolve</span>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={() => handleDelete(id)}
                    className="gap-2 cursor-pointer text-rose-600 hover:text-rose-700"
                  >
                    <Trash2 className="w-4 h-4 text-rose-500" />
                    <span>Delete</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      },
    ];
  }, [params.page]);

  return (
    <>
      {/* Compact Single Toolbar Row */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        {/* Left: Search Input */}
        <div className="relative w-72">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input
            placeholder="Search passenger, driver, route..."
            value={newParam.get("search") || ""}
            onChange={(e) => handleFilter("search", e.target.value)}
            className="search-icon-input h-9 text-sm bg-white border-slate-200 focus:bg-white shadow-2xs"
          />
        </div>

        {/* Right: Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Status Filter */}
          <div className="w-36">
            <Select
              value={query.get("is_resolved") ?? "ALL"}
              onValueChange={(val) => handleFilter("is_resolved", val === "ALL" ? "" : val)}
            >
              <SelectTrigger className="h-9 text-sm bg-white shadow-2xs">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Status</SelectItem>
                <SelectItem value="1">Resolved</SelectItem>
                <SelectItem value="0">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* AG Grid Table */}
      <AgGridTable
        rowData={report}
        columnDefs={columnDefs}
        loading={loading}
        domLayout="autoHeight"
        emptyMessage="No reported rides found"
      />

      <div className="mt-3">
        <PaginationLayout
          count={totalCount}
          data={report}
          page={Number(params.page)}
          limit={Number(limit)}
          loading={loading}
          onPageChange={(val) => handlePagination(val)}
        />
      </div>

      {/* Read Message Dialog */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Report Reason / Query</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed break-words bg-slate-50 p-3.5 rounded-lg border border-slate-100">
              {readContent}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReportedRidesPage;
